import {useMemo} from 'react';
import useEhrPatientSearch from '../common/useEhrPatientSearch';
import useUnifiedPatients from '../common/useUnifiedPatients';
import {patientSearchShape} from './createSessionFormUtils';
import {
  buildLocalPatientBrowserItems,
  dedupePlannerPatientItems,
} from './plannerPatientBrowser';
import useHydratedEhrPatients from './useHydratedEhrPatients';
import {formatDobShort, isNonEnglish} from '../../constants/designTokens';
import {searchTextMatchTokenCount} from '../../utils/searchText';
import {isMockEhrEnabled} from '../../mocks/mockEhrFixtures';

const SEARCH_RESULT_LIMIT = 12;

const patientItemSearchText = (item: any) => {
  const shape = patientSearchShape(item);
  const dob = formatDobShort(shape.dob || shape.date_of_birth);
  const mrn = shape.mrn || shape.accountNo;
  return [
    shape.first_name,
    shape.middleName,
    shape.last_name,
    isNonEnglish(shape.preferred_language) ? shape.preferred_language : '',
    dob ? `DOB ${dob}` : '',
    mrn ? `MRN ${mrn}` : '',
  ]
    .filter(Boolean)
    .join(' ');
};

export default function usePlannerPatientBrowser({
  userData,
  searchQuery = '',
}: Record<string, any> = {}) {
  const companyId = userData?.companyId || null;
  const isMockEhrMode = isMockEhrEnabled(userData);

  // One roster load for every surface. Always fetch local patients so they
  // surface alongside EHR results in Search and the patient roster, regardless
  // of whether the company has an active EHR integration.
  const {
    hasActiveEhr: linkedOrMockEhr,
    hasLoaded: hasLoadedUnifiedPatients,
    hasLoadedEhrConfig: hasLoadedEhrConfigFlag,
    isLoading: isLoadingLocalPatients,
    error: localPatientsError,
    buildUnifiedPatients,
  } = useUnifiedPatients({
    userData,
    clearWhenDisabled: true,
    fetchLocalPatients: true,
  });
  const hasActiveEhr = linkedOrMockEhr;
  const networkEhrEnabled = hasActiveEhr && !isMockEhrMode;
  // The config flag settles before the roster; fall back to the full-load
  // flag for callers (and test doubles) that don't provide it.
  const hasLoadedEhrConfig = hasLoadedEhrConfigFlag ?? hasLoadedUnifiedPatients;

  const {
    canTriggerManualDeepSearch,
    deepSearchCompletion,
    deepSearchError,
    deepSearchSlow,
    deepSearchUnavailable,
    ehrPatients: rawSearchEhrPatients,
    isDeepSearching,
    isSearching: isSearchingEhrPatients,
    searchError,
    triggerDeepSearch,
  } = useEhrPatientSearch({
    autoDeepSearch: true,
    autoDeepSearchMode: 'settled',
    companyId,
    hasActiveEhr: networkEhrEnabled,
    searchQuery,
    cachedPatients: [],
  });

  // Live-search (Ayden) hits arrive without demographics; backfill them from
  // the EHR cache by source id so EHR-only patients render language/DOB/MRN
  // like cached or encounter-backed patients instead of a bare name.
  const searchEhrPatients = useHydratedEhrPatients(
    rawSearchEhrPatients,
    companyId,
    networkEhrEnabled,
  );

  const searchPatientItems = useMemo(() => {
    const query = searchQuery.trim();
    if (!query) return [];
    // Local (incl. linked) + live EHR search results. Exact demographic
    // duplicates are collapsed by buildUnifiedPatients; EHR hits are ignored
    // when no active EHR integration is available.
    const unified = buildUnifiedPatients(searchEhrPatients);

    let matches = [];
    for (const item of unified) {
      if (searchTextMatchTokenCount(patientItemSearchText(item), query) <= 0)
        continue;
      matches.push(item);
      matches = dedupePlannerPatientItems(matches);
      if (matches.length >= SEARCH_RESULT_LIMIT) {
        return matches.slice(0, SEARCH_RESULT_LIMIT);
      }
    }

    return matches.slice(0, SEARCH_RESULT_LIMIT);
  }, [buildUnifiedPatients, searchEhrPatients, searchQuery]);

  const localPatientItems = useMemo(
    () => buildLocalPatientBrowserItems(buildUnifiedPatients()),
    [buildUnifiedPatients],
  );

  return {
    hasActiveEhr,
    hasLoadedEhrConfig,
    canTriggerManualDeepSearch,
    deepSearchCompletion,
    deepSearchError,
    deepSearchSlow,
    deepSearchUnavailable,
    isDeepSearching,
    isLoadingLocalPatients,
    isSearchingEhrPatients,
    localPatientItems,
    localPatientsError,
    searchError,
    searchPatientItems,
    triggerDeepSearch,
  };
}
